import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";

import { trackComponentView } from "../../telemetry/telemetry";
import { RootState } from "../../app/rootReducer";
import { isNoFilterApplied } from "../../searchSideBar/queryStringHelpers";
import {
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
  setQueryString,
  sumPropertyFilterQueries,
} from "../../searchSideBar/searchSideBarConnector";
import { setValidationPageNumber } from "../asinValidationTableConnector";
import { HideAllPassingToggle } from "./HideAllPassingToggle";
import { PipelineFilter } from "./PipelineFilter";
import { PivotTable } from "./PivotTable";
import { shouldHideColumn } from "./hideAllPassing";
import {
  PipelineFilterSelection,
  shouldRenderColumnUnderPipelineFilter,
} from "./pipelineFilterVisibility";
import {
  buildPivotFromAggregation,
  pivotCellKey,
  PivotRow,
  RowGroupPath,
} from "./stepBreakdown";
import { fetchStepBreakdownAggregation } from "./stepBreakdownAggregationConnector";
import {
  applyStepDrilldownFilters,
  StepFilterClick,
} from "./stepFilterTokenToggle";

const GRAND_TOTAL_KEY = "grandTotal";

function encodeRowGroupPath(path: RowGroupPath): string {
  if (path.length === 0) {
    return GRAND_TOTAL_KEY;
  }
  return path.map((t) => `${t.level}:${t.value}`).join("/");
}

function collectVisibleRows(
  row: PivotRow,
  expanded: ReadonlySet<string>,
  out: PivotRow[]
): void {
  out.push(row);
  if (!expanded.has(encodeRowGroupPath(row.path))) {
    return;
  }
  for (const child of row.children) {
    collectVisibleRows(child, expanded, out);
  }
}

function stepFilterClickEqual(a: StepFilterClick, b: StepFilterClick): boolean {
  if (a.pipeline !== b.pipeline || a.stepName !== b.stepName) {
    return false;
  }
  if (a.path.length !== b.path.length) {
    return false;
  }
  for (let i = 0; i < a.path.length; i++) {
    if (
      a.path[i].level !== b.path[i].level ||
      a.path[i].value !== b.path[i].value
    ) {
      return false;
    }
  }
  return true;
}

export const StepBreakdownPivot: React.FC = () => {
  const dispatch = useDispatch();

  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );
  const queryPropertyFilters = useSelector(
    (state: RootState) => state.generateQuery.queryPropertyFilters
  );
  const { status, response, error } = useSelector(
    (state: RootState) => state.stepBreakdownAggregation
  );

  const [expandedRowPaths, setExpandedRowPaths] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );
  const [hideAllPassing, setHideAllPassing] = useState<boolean>(true);
  const [pipelineFilter, setPipelineFilter] =
    useState<PipelineFilterSelection>("ALL");
  const [activeCell, setActiveCell] = useState<StepFilterClick | undefined>(
    undefined
  );

  useEffect(() => {
    trackComponentView("stepBreakdown", "view");
  }, []);

  // Records the exact queryString we expect the store to hold after our
  // own onCellClick runs. The reset effect below skips a single fire
  // whose incoming queryString matches this ref, so a click doesn't
  // clobber the activeCell it just set. External filter changes don't
  // match, so they still reset the pivot's local UI state as intended.
  const skipResetForQueryStringRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Consume the armed value on every fire, matching or not. The click
    // dispatches (setQueryPropertyFilters + setQueryString) are batched
    // into a single effect fire, so disarming here is always correct.
    // Leaving the ref armed after a non-matching fire would let a
    // later external change that happens to produce the same
    // queryString silently swallow its reset -- setQueryString has a
    // pendingQueryString branch that can produce a value we didn't
    // predict from the click's PropertyFilter alone.
    const armed = skipResetForQueryStringRef.current;
    skipResetForQueryStringRef.current = undefined;
    if (armed !== undefined && armed === queryString) {
      return;
    }
    setExpandedRowPaths(new Set<string>());
    setHideAllPassing(true);
    setPipelineFilter("ALL");
    setActiveCell(undefined);
  }, [queryString]);

  const zeroState = isNoFilterApplied(queryString);

  const pivot = useMemo(() => {
    if (response === undefined) {
      return undefined;
    }
    return buildPivotFromAggregation(response);
  }, [response]);

  const visibleRows = useMemo<readonly PivotRow[]>(() => {
    if (pivot === undefined) {
      return [];
    }
    const rows: PivotRow[] = [];
    for (const topRow of pivot.grandTotal.children) {
      collectVisibleRows(topRow, expandedRowPaths, rows);
    }
    // Include the grand total in the predicate input so a step whose
    // top-line pass rate is 100 still gets hidden by hide-all-passing.
    rows.push(pivot.grandTotal);
    return rows;
  }, [pivot, expandedRowPaths]);

  const activeStepFilterColumnKeys = useMemo<ReadonlySet<string>>(() => {
    if (activeCell === undefined) {
      return new Set<string>();
    }
    return new Set<string>([
      pivotCellKey({
        pipeline: activeCell.pipeline,
        stepName: activeCell.stepName,
      }),
    ]);
  }, [activeCell]);

  const hiddenColumns = useMemo<ReadonlySet<string>>(() => {
    if (pivot === undefined) {
      return new Set<string>();
    }
    const hidden = new Set<string>();
    for (const col of pivot.columns) {
      const columnKey = pivotCellKey(col);
      const admittedByPipeline = shouldRenderColumnUnderPipelineFilter(
        col,
        pipelineFilter,
        activeStepFilterColumnKeys
      );
      if (!admittedByPipeline) {
        hidden.add(columnKey);
        continue;
      }
      if (
        hideAllPassing &&
        !activeStepFilterColumnKeys.has(columnKey) &&
        shouldHideColumn(col, visibleRows)
      ) {
        hidden.add(columnKey);
      }
    }
    return hidden;
  }, [
    pivot,
    hideAllPassing,
    pipelineFilter,
    activeStepFilterColumnKeys,
    visibleRows,
  ]);

  const noColumnsReason = useMemo<
    "pipeline" | "hide-all-passing" | undefined
  >(() => {
    if (pivot === undefined) {
      return undefined;
    }
    if (pivot.columns.length === 0) {
      return undefined;
    }
    if (hiddenColumns.size !== pivot.columns.length) {
      return undefined;
    }
    if (
      pipelineFilter !== "ALL" &&
      pivot.columns.some(
        (c) =>
          c.pipeline !== pipelineFilter &&
          !activeStepFilterColumnKeys.has(pivotCellKey(c))
      )
    ) {
      return "pipeline";
    }
    if (hideAllPassing) {
      return "hide-all-passing";
    }
    return undefined;
  }, [
    pivot,
    hiddenColumns,
    pipelineFilter,
    hideAllPassing,
    activeStepFilterColumnKeys,
  ]);

  const onToggleExpanded = useCallback((path: RowGroupPath) => {
    const key = encodeRowGroupPath(path);
    setExpandedRowPaths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const onCellClick = useCallback(
    (click: StepFilterClick) => {
      const isToggleOff =
        activeCell !== undefined && stepFilterClickEqual(activeCell, click);
      const nextActiveCell = isToggleOff ? undefined : click;
      const nextFilters = applyStepDrilldownFilters(
        queryPropertyFilters,
        nextActiveCell?.path
      );
      // Arm the guard with the exact queryString this click will
      // produce so the reset effect can identify and skip the
      // resulting fire. When the produced value equals the current
      // queryString (e.g. Grand-Total click with an empty path where
      // both previousPath and nextPath are empty), the reducer stores
      // the same value and the effect never fires — so leave the ref
      // clear rather than armed with a stale value that would swallow
      // the next external reset.
      const nextQueryString = sumPropertyFilterQueries(nextFilters).toString();
      skipResetForQueryStringRef.current =
        nextQueryString === queryString ? undefined : nextQueryString;
      setActiveCell(nextActiveCell);
      dispatch(setQueryPropertyFilters(nextFilters));
      dispatch(setQueryPropertyFiltersForceRehydrate(true));
      dispatch(setQueryString());
      dispatch(setValidationPageNumber({ pageNumber: 1 }));
    },
    [dispatch, queryPropertyFilters, activeCell, queryString]
  );

  const onRetry = useCallback(() => {
    if (queryString === undefined || queryString === "") {
      return;
    }
    dispatch(fetchStepBreakdownAggregation(queryString));
  }, [dispatch, queryString]);

  const toolbar = (
    <div
      style={{
        display: "flex",
        gap: "1rem",
        alignItems: "center",
        marginBottom: "1rem",
      }}
    >
      <HideAllPassingToggle on={hideAllPassing} onChange={setHideAllPassing} />
      <PipelineFilter value={pipelineFilter} onChange={setPipelineFilter} />
    </div>
  );

  if (zeroState) {
    return (
      <div>
        {toolbar}
        <Box
          data-testid="step-breakdown-zero-state"
          variant="p"
          color="text-body-secondary"
          textAlign="center"
        >
          Apply a filter to view the Step Breakdown
        </Box>
      </div>
    );
  }

  const hasPriorResponse = pivot !== undefined;

  if (status === "loading" && !hasPriorResponse) {
    return (
      <div>
        {toolbar}
        <Box
          data-testid="step-breakdown-loading"
          variant="p"
          color="text-body-secondary"
          textAlign="center"
        >
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Spinner />
            <span>Loading Step Breakdown…</span>
          </SpaceBetween>
        </Box>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div>
        {toolbar}
        <SpaceBetween size="s" direction="vertical">
          <Box
            data-testid="step-breakdown-error"
            variant="p"
            color="text-status-error"
          >
            Could not load Step Breakdown: {error ?? "unknown error"}
          </Box>
          <Button data-testid="step-breakdown-retry" onClick={onRetry}>
            Retry
          </Button>
        </SpaceBetween>
      </div>
    );
  }

  if (
    status !== "loading" &&
    pivot !== undefined &&
    response !== undefined &&
    response.totalCount === 0
  ) {
    return (
      <div>
        {toolbar}
        <Box
          data-testid="step-breakdown-no-matching-data"
          variant="p"
          color="text-body-secondary"
          textAlign="center"
        >
          No matching data for the current filter.
        </Box>
      </div>
    );
  }

  if (pivot === undefined) {
    return <div>{toolbar}</div>;
  }

  if (noColumnsReason !== undefined) {
    const pipelineLabel = pipelineFilter === "LVS" ? "LVS" : "Hattrick";
    const message =
      noColumnsReason === "pipeline"
        ? `No ${pipelineLabel} steps in the current filter.`
        : "All steps are passing under Hide-all-passing.";
    return (
      <div>
        {toolbar}
        <Box
          data-testid="step-breakdown-no-columns-visible"
          data-no-columns-reason={noColumnsReason}
          variant="p"
          color="text-body-secondary"
          textAlign="center"
        >
          {message}
        </Box>
      </div>
    );
  }

  const showSubordinateLoading = status === "loading" && hasPriorResponse;

  return (
    <div>
      {toolbar}
      {showSubordinateLoading && (
        <Box
          data-testid="step-breakdown-loading-subordinate"
          variant="p"
          color="text-body-secondary"
        >
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Spinner />
            <span>Refreshing…</span>
          </SpaceBetween>
        </Box>
      )}
      <PivotTable
        columns={pivot.columns}
        rows={pivot.grandTotal.children}
        grandTotalRow={pivot.grandTotal}
        expandedRowPaths={expandedRowPaths}
        onToggleExpanded={onToggleExpanded}
        onCellClick={onCellClick}
        activeStepFilter={activeCell}
        hiddenColumns={hiddenColumns}
      />
    </div>
  );
};

export default StepBreakdownPivot;
